const pad2 = (n: number): string => String(n).padStart(2, '0');

const toValidDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

export const formatDate = (value: string | Date | null | undefined): string => {
  const d = toValidDate(value);
  if (!d) return '—';
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

export const formatDateTime = (value: string | Date | null | undefined): string => {
  const d = toValidDate(value);
  if (!d) return '—';
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

type RelativeUnit = 's' | 'm' | 'h' | 'd';

const RELATIVE_THRESHOLDS: { limit: number; divisor: number; unit: RelativeUnit }[] = [
  { limit: 60, divisor: 1, unit: 's' },
  { limit: 3600, divisor: 60, unit: 'm' },
  { limit: 86400, divisor: 3600, unit: 'h' },
  { limit: 604800, divisor: 86400, unit: 'd' },
];

/** Short unit suffixes per UI language ("5 мин", "3 сағ", "2d"). */
const EN_SUFFIXES: Record<RelativeUnit, string> = { s: 's', m: 'm', h: 'h', d: 'd' };

const RELATIVE_SUFFIXES: Record<string, Record<RelativeUnit, string>> = {
  uz: { s: ' son', m: ' daq', h: ' soat', d: ' kun' },
  ru: { s: ' сек', m: ' мин', h: ' ч', d: ' дн' },
  en: EN_SUFFIXES,
  kk: { s: ' сек', m: ' мин', h: ' сағ', d: ' күн' },
};

export type RelativeTime = { kind: 'now' } | { kind: 'elapsed'; value: string } | { kind: 'date'; value: string };

export const formatRelativeTime = (
  value: string | Date | null | undefined,
  lang?: string,
): RelativeTime => {
  const d = toValidDate(value);
  if (!d) return { kind: 'now' };
  const elapsedSeconds = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (elapsedSeconds < 1) return { kind: 'now' };
  const base = (lang ?? '').toLowerCase().split('-')[0] ?? '';
  const suffixes = RELATIVE_SUFFIXES[base] ?? EN_SUFFIXES;
  for (const { limit, divisor, unit } of RELATIVE_THRESHOLDS) {
    if (elapsedSeconds < limit) {
      return { kind: 'elapsed', value: `${Math.floor(elapsedSeconds / divisor)}${suffixes[unit]}` };
    }
  }
  return { kind: 'date', value: formatDateTime(d) };
};
